import { confirm } from '@/components/ui/confirmation-dialog';
import { Business } from '@/types/business';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Pressable, Text, View } from 'react-native';

interface AdminPendingBusinessRowProps {
  business: Business;
  onApprove: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function AdminPendingBusinessRow({
  business,
  onApprove,
  onEdit,
  onDelete,
}: AdminPendingBusinessRowProps) {
  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete business?',
      description: `Delete "${business.name}"? This cannot be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      onDelete(business.id);
    }
  };

  const formattedDate = new Date(business.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <View
      className="bg-surface"
      style={{ borderRadius: 12, padding: 14, marginBottom: 10 }}
    >
      <View style={{ marginBottom: 10 }}>
        <Text className="text-foreground" style={{ fontSize: 16, fontWeight: '600' }}>
          {business.name}
        </Text>
        {business.city && (
          <Text className="text-sm mt-0.5 text-muted-foreground">
            {business.city}{business.state ? `, ${business.state}` : ''}
          </Text>
        )}
        {business.address && (
          <Text
            className="text-sm mt-0.5 text-muted-foreground"
            numberOfLines={1}
          >
            {business.address}
          </Text>
        )}
        {business.cuisineType && (
          <Text className="text-xs mt-0.5 text-muted-foreground">
            {business.cuisineType.replace(/_/g, ' ')}
          </Text>
        )}
        <Text className="text-xs mt-1 text-muted-foreground">
          Submitted {formattedDate}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Pressable
          onPress={() => onApprove(business.id)}
          accessibilityRole="button"
          accessibilityLabel="Approve business"
          className="bg-primary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="checkmark" size={18} className="text-background" />
          <Text className="text-sm font-semibold text-background">
            Approve
          </Text>
        </Pressable>

        <Pressable
          onPress={() => onEdit(business.id)}
          accessibilityRole="button"
          accessibilityLabel="Edit business"
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="create-outline" size={18} className="text-foreground" />
          <Text className="text-sm font-semibold text-foreground">Edit</Text>
        </Pressable>

        <Pressable
          onPress={handleDelete}
          accessibilityRole="button"
          accessibilityLabel="Delete business"
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="trash-outline" size={18} className="text-error" />
          <Text className="text-sm font-semibold text-error">Delete</Text>
        </Pressable>
      </View>
    </View>
  );
}
